import useForceUpdate from "PFCore/helpers/use_force_update";
import { MutableRefObject, useCallback, useEffect, useState } from "react";

import { getMaxValues, getValueWithinMaxMin } from "./helpers";

const useScroll = (
  thumbWidth: MutableRefObject<number>,
  thumbLeft: MutableRefObject<number>,
  calendarElement: HTMLElement,
  scrollToLeftOffset: (value: number) => void
) => {
  const [clickedClientX, setClickedClientX] = useState(0);
  const [initialThumbLeftPosition, setInitialThumbLeftPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const forceUpdate = useForceUpdate();

  const handleThumbMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setInitialThumbLeftPosition(thumbLeft.current);
    setClickedClientX(e.clientX);
    setIsDragging(true);
  }, []);

  const handleDocumentMouseUp = useCallback(
    (e) => {
      if (isDragging) {
        e.preventDefault();
        setIsDragging(false);
      }
    },
    [isDragging]
  );

  const handleDocumentMouseMove = useCallback(
    (event) => {
      if (isDragging) {
        event.preventDefault();
        event.stopPropagation();
        const deltaX = event.clientX - clickedClientX;
        const { maxScrollLeft, maxThumbLeft } = getMaxValues(thumbWidth.current, calendarElement);
        const newThumbLeft = initialThumbLeftPosition + deltaX;
        // from the ratio: newScrollLeft/maxScrollLeft === newThumbLeft/maxThumbLeft
        const newScrollLeftFromRatio = (newThumbLeft / maxThumbLeft) * maxScrollLeft;
        const newScrollLeft =
          maxThumbLeft === 0 ? 0 : getValueWithinMaxMin(newScrollLeftFromRatio, maxScrollLeft);
        scrollToLeftOffset(newScrollLeft);
      }
    },
    [isDragging, initialThumbLeftPosition]
  );

  const handleCalendarScroll = useCallback(() => {
    if (calendarElement) {
      const { scrollLeft } = calendarElement;
      const { maxScrollLeft, maxThumbLeft } = getMaxValues(thumbWidth.current, calendarElement);
      // from the ratio: newThumbLeft/maxThumbLeft === scrollLeft/maxScrollLeft
      const newThumbLeft = (scrollLeft / maxScrollLeft) * maxThumbLeft;
      thumbLeft.current = maxScrollLeft === 0 ? 0 : getValueWithinMaxMin(newThumbLeft, maxThumbLeft);
      forceUpdate();
    }
  }, [calendarElement]);

  useEffect(() => {
    if (calendarElement) {
      calendarElement.addEventListener("scroll", handleCalendarScroll, true);
      return () => {
        calendarElement.removeEventListener("scroll", handleCalendarScroll, true);
      };
    }
  }, [handleCalendarScroll]);

  useEffect(() => {
    document.addEventListener("mousemove", handleDocumentMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);
    document.addEventListener("mouseleave", handleDocumentMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
      document.removeEventListener("mouseleave", handleDocumentMouseUp);
    };
  }, [handleDocumentMouseMove, handleDocumentMouseUp]);

  return { isDragging, handleThumbMouseDown };
};

export default useScroll;
